import { JsonObject, JsonProperty } from "typescript-json-serializer";
import {WidgetConfiguration} from "./widget-configuration";
import Collection from "@arcgis/core/core/Collection";

@JsonObject()
export class SketchDefaultUpdateOptionsConfiguration implements __esri.SketchDefaultUpdateOptions {
    /**
     * Name of the update tool.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#defaultUpdateOptions)
     */
    @JsonProperty()
    declare tool?: "move" | "transform" | "reshape";
    /**
     * Indicates if the `rotation` operation will be enabled when updating graphics.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#defaultUpdateOptions)
     */
    @JsonProperty()
    declare enableRotation?: boolean;
    /**
     * Indicates if the `scale` operation will be enabled when updating graphics.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#defaultUpdateOptions)
     */
    @JsonProperty()
    declare enableScaling?: boolean;
    /**
     * Indicates if z-values can be modified when updating the graphic.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#defaultUpdateOptions)
     */
    @JsonProperty()
    declare enableZ?: boolean;
    /**
     * Indicates whether more than one selection can be made at once.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#defaultUpdateOptions)
     */
    @JsonProperty()
    declare multipleSelectionEnabled?: boolean;
    /**
     * Indicates if the uniform scale operation will be enabled when updating graphics.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#defaultUpdateOptions)
     */
    @JsonProperty()
    declare preserveAspectRatio?: boolean;
    /**
     * Indicates if the graphic being updated can be toggled between `transform` and `reshape` update options.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#defaultUpdateOptions)
     */
    @JsonProperty()
    declare toggleToolOnClick?: boolean;
    /**
     * Changes the behavior for the `reshape` tool.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#defaultUpdateOptions)
     */
    // @JsonProperty()
    // declare reshapeOptions?: SketchDefaultUpdateOptionsReshapeOptions;
    /**
     * Options that control when to display or hide highlights for update operations.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#defaultUpdateOptions)
     */
    // @JsonProperty()
    // declare highlightOptions?: SketchDefaultUpdateOptionsHighlightOptions;

    constructor() {}
}

@JsonObject()
export class SketchDefaultCreateOptionsConfiguration implements __esri.SketchDefaultCreateOptions {
    /**
     * Create operation mode how the graphic can be created.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#defaultCreateOptions)
     */
    @JsonProperty()
    declare mode?: "hybrid" | "freehand" | "click";
    /**
     * Controls whether the created geometry has z-values or not.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#defaultCreateOptions)
     */
    @JsonProperty()
    declare hasZ?: boolean;
    /**
     * The default z-value of the newly created geometry.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#defaultCreateOptions)
     */
    @JsonProperty()
    declare defaultZ?: number;

    constructor() {}
}
@JsonObject()
export class SketchLabelOptionsConfiguration {
    /**
     * Whether labels are shown next to each segment of the graphic being sketched.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchLabelOptions.html#enabled)
     */
    @JsonProperty()
    declare enabled?: boolean;
}
/**
 * The elements that are displayed within the tooltip while sketching and editing.
 *
 * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#VisibleElements)
 */
@JsonObject()
export class VisibleElementsConfiguration {
    /**
     * When set to `false`, the area is not shown in the tooltip.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#VisibleElements)
     */
    @JsonProperty()
    declare area?: boolean;
    /**
     * When set to `false`, the coordinate is not shown in the tooltip.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#VisibleElements)
     */
    @JsonProperty()
    declare coordinates?: boolean;
    /**
     * When set to `false`, the direction (or deflection angle) is not shown in the tooltip.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#VisibleElements)
     */
    @JsonProperty()
    declare direction?: boolean;
    /**
     * When set to `false`, the distance is not shown in the tooltip.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#VisibleElements)
     */
    @JsonProperty()
    declare distance?: boolean;
    /**
     * When set to `false`, the elevation is not shown in the tooltip.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#VisibleElements)
     */
    @JsonProperty()
    declare elevation?: boolean;
    /**
     * When set to `false`, the header at the top of the tooltip is not shown, when in input mode.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#VisibleElements)
     */
    @JsonProperty()
    declare header?: boolean;
    /**
     * When set to `false`, the help message at the bottom of the tooltip is not shown.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#VisibleElements)
     */
    @JsonProperty()
    declare helpMessage?: boolean;
    /**
     * When set to `false`, the orientation is not shown in the tooltip.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#VisibleElements)
     */
    @JsonProperty()
    declare orientation?: boolean;
    /**
     * When set to `false`, the radius is not shown in the tooltip.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#VisibleElements)
     */
    @JsonProperty()
    declare radius?: boolean;
    /**
     * When set to `false`, the rotation is not shown in the tooltip.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#VisibleElements)
     */
    @JsonProperty()
    declare rotation?: boolean;
    /**
     * When set to `false`, the scale is not shown in the tooltip.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#VisibleElements)
     */
    @JsonProperty()
    declare scale?: boolean;
    /**
     * When set to `false`, the size is not shown in the tooltip.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#VisibleElements)
     */
    @JsonProperty()
    declare size?: boolean;
    /**
     * When set to `false`, the total length is not shown in the tooltip.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#VisibleElements)
     */
    @JsonProperty()
    declare totalLength?: boolean;
}

@JsonObject()
export class SketchValueOptionsConfiguration {
    /**
     * How direction values are displayed and, in case of input, how they are interpreted.
     *
     * @default "relative"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchValueOptions.html#directionMode)
     */
    @JsonProperty()
    declare directionMode?: "relative" | "absolute";

    /**
     * Units used for displaying values in the UI.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchValueOptions.html#displayUnits)
     */
    @JsonProperty()
    declare displayUnits?: __esri.Units;
    /**
     * Units used for interpreting input values.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchValueOptions.html#inputUnits)
     */
    @JsonProperty()
    declare inputUnits?: __esri.Units;
}
@JsonObject()
export class SketchTooltipOptionsConfiguration {
    /**
     * Whether tooltips are shown while sketching and editing.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#enabled)
     */
    @JsonProperty()
    declare enabled?: boolean;
    /**
     * A custom help message that is displayed at the bottom of the tooltip.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#helpMessage)
     */
    @JsonProperty()
    declare helpMessage?: string;
    /**
     * An icon that is displayed next to the help message at the bottom of the tooltip.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#helpMessageIcon)
     */
    @JsonProperty()
    declare helpMessageIcon?: string;
    /**
     * Whether users can focus the tooltip and input constraint values.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#inputEnabled)
     */
    @JsonProperty()
    declare inputEnabled?: boolean;
    /**
     * The elements that are displayed within the tooltip while sketching.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-sketch-SketchTooltipOptions.html#visibleElements)
     */
    @JsonProperty()
    declare visibleElements?: VisibleElementsConfiguration;
}
@JsonObject()
export class VisibleElementsCreateToolsConfiguration {
    /**
     * Indicates whether to display the point sketch tool.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#VisibleElements)
     */
    @JsonProperty()
    declare point?: boolean;
    /**
     * Indicates whether to display the polyline sketch tool.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#VisibleElements)
     */
    @JsonProperty()
    declare polyline?: boolean;
    /**
     * Indicates whether to display the polygon sketch tool.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#VisibleElements)
     */
    @JsonProperty()
    declare polygon?: boolean;
    /**
     * Indicates whether to display the rectangle sketch tool.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#VisibleElements)
     */
    @JsonProperty()
    declare rectangle?: boolean;
    /**
     * Indicates whether to display the circle sketch tool.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#VisibleElements)
     */
    @JsonProperty()
    declare circle?: boolean;

    constructor() {}
}

@JsonObject()
export class VisibleElementsSelectionToolsConfiguration {
    /**
     * Indicates whether to display the `"rectangle-selection"` tool.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#VisibleElements)
     */
    @JsonProperty()
    declare "rectangle-selection"?: boolean;
    /**
     * Indicates whether to display the `"lasso-selection"` tool.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#VisibleElements)
     */
    @JsonProperty()
    declare "lasso-selection"?: boolean;

    constructor() {}
}

@JsonObject()
export class SketchVisibleElementsConfiguration {
    /**
     * The available sketch tools within the widget.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#VisibleElements)
     */
    @JsonProperty()
    declare createTools?: VisibleElementsCreateToolsConfiguration;
    /**
     * Indicates whether to display the 'duplicate' button while a graphic is selected.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#VisibleElements)
     */
    @JsonProperty()
    declare duplicateButton?: boolean;
    /**
     * The available selection tools within the widget.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#VisibleElements)
     */
    @JsonProperty()
    declare selectionTools?: VisibleElementsSelectionToolsConfiguration;
    /**
     * Indicates whether to display the settings menu.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#VisibleElements)
     */
    @JsonProperty()
    declare settingsMenu?: boolean;
    /**
     * _Since 4.29_.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#VisibleElements)
     */
    @JsonProperty()
    declare labelsToggle?: boolean;
    /**
     * _Since 4.29_.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#VisibleElements)
     */
    @JsonProperty()
    declare tooltipsToggle?: boolean;
    /**
     * Indicates whether to display the `SnappingControls` widget.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#VisibleElements)
     */
    @JsonProperty()
    declare snappingControls?: boolean;
    /**
     * The available [SnappingControls](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-support-SnappingControls.html) elements within the widget.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#VisibleElements)
     */
    //snappingControlsElements?: SketchVisibleElementsSnappingControlsElements;
    /**
     * Indicates whether to display the undo/redo menu within the widget.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#VisibleElements)
     */
    @JsonProperty()
    declare undoRedoMenu?: boolean;
    constructor() {}
}
export class FeatureSnappingLayerSourceConfiguration {
    /**
     * Indicates whether feature snapping is turned on or off.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-snapping-FeatureSnappingLayerSource.html#enabled)
     */
    @JsonProperty()
    declare enabled?: boolean;
    /**
     * The source layer used for snapping.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-snapping-FeatureSnappingLayerSource.html#layer)
     */
    /* layer?: | (BuildingSceneLayerProperties & { type: "building-scene" })
    | (CSVLayerProperties & { type: "csv" })
    | (FeatureLayerProperties & { type: "feature" })
    | (GeoJSONLayerProperties & { type: "geojson" })
    | (GraphicsLayerProperties & { type: "graphics" })
    | (MapNotesLayerProperties & { type: "map-notes" })
    | (SceneLayerProperties & { type: "scene" })
    | (WFSLayerProperties & { type: "wfs" }); */
}
@JsonObject()
export class SnappingOptionsConfiguration {
    /**
     * Snapping distance for snapping in pixels.
     *
     * @default 5
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-snapping-SnappingOptions.html#distance)
     */
    @JsonProperty()
    declare distance?: number;
    /**
     * Global configuration to turn snapping on or off.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-snapping-SnappingOptions.html#enabled)
     */
    @JsonProperty()
    declare enabled?: boolean;
    /**
     * Global configuration option to turn feature snapping on or off.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-snapping-SnappingOptions.html#featureEnabled)
     */
    @JsonProperty()
    declare featureEnabled?: boolean;
    /**
     * List of sources for feature snapping.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-snapping-SnappingOptions.html#featureSources)
     */
    @JsonProperty()
    declare featureSources?: Collection<FeatureSnappingLayerSourceConfiguration>;

    /**
     * Turns the grid on or off.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-snapping-SnappingOptions.html#gridEnabled)
     */
    @JsonProperty()
    declare gridEnabled?: boolean;

    /**
     * Global configuration option to turn self snapping (within one feature while either drawing or reshaping) on or off.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-snapping-SnappingOptions.html#selfEnabled)
     */
    @JsonProperty()
    declare selfEnabled?: boolean;
}
@JsonObject()
export class SymbolConfiguration {
    @JsonProperty()
    declare activeFill: __esri.SimpleFillSymbolProperties;

    @JsonProperty()
    declare polygon: __esri.SimpleFillSymbolProperties;

    @JsonProperty()
    declare polyline: __esri.SimpleLineSymbolProperties;

    constructor() {
    }
}
@JsonObject()
export class SketchConfiguration extends WidgetConfiguration {
    /**
     * Geo.js property to place the widget in one of the Esri UI corners.
     */
    @JsonProperty()
    declare position: "top-left" | "top-right" | "bottom-left" | "bottom-right";
    /**
     * Geo.js property to limit the maximum object count.
     */
    @JsonProperty()
    declare maxObject?: number;
    @JsonProperty()
    declare symbols?: SymbolConfiguration;

    /**
     * When creating new graphics (for example after create() has been called), this property reflects the create tool being used.
     * When updating graphics (for example after update() has been called), this property reflects the update tool being used.
     * If no create or update operation is in progress, this is null.
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#activeTool)
     */
    @JsonProperty()
    declare activeTool: "point" | "polyline" | "polygon" | "circle" | "rectangle" | "move" | "transform" | "reshape" | "rectangle-selection" | "lasso-selection";

    /**
     * Property controlling the visibility and order of create tool buttons.
     *
     * @default ["point", "polyline", "polygon", "rectangle", "circle"]
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#availableCreateTools)
     */
    @JsonProperty()
    declare availableCreateTools: string[];

    /**
     * Defines the default behavior once the [create](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#create) operation is completed.
     *
     * @default "continuous"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#creationMode)
     */
    @JsonProperty()
    declare creationMode?: "single" | "continuous" | "update";

    /**
     * Default create options set for the Sketch widget.
     * Possible Values: "hybrid" | "freehand" | "click"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#defaultCreateOptions)
     */
    @JsonProperty()
    declare defaultCreateOptions: SketchDefaultCreateOptionsConfiguration;

    /**
     * Default update options set for the Sketch widget. Update options set on this property will be overwritten
     * if the update options are changed when update() method is called.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#defaultUpdateOptions)
     */
    @JsonProperty()
    declare defaultUpdateOptions?: SketchDefaultUpdateOptionsConfiguration;

    /**
     * Options to configure the sketch labels shown next to each segment of the geometry being created or updated.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#labelOptions)
     */
    @JsonProperty()
    declare labelOptions?: SketchLabelOptionsConfiguration;

    /**
     * Determines the layout/orientation of the Sketch widget.
     *
     * @default "horizontal"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#layout)
     */
    @JsonProperty()
    declare layout?: "vertical" | "horizontal";
    /**
     * The [SnappingOptions](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-interactive-snapping-SnappingOptions.html) for sketching.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#snappingOptions)
     */
    @JsonProperty()
    declare snappingOptions?: SnappingOptionsConfiguration;
    /**
     * Options to configure the tooltip shown next to the cursor when creating or updating graphics.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#tooltipOptions)
     */
    @JsonProperty()
    declare tooltipOptions?: SketchTooltipOptionsConfiguration;
    /**
     * Options to configure how values are displayed and input when creating or updating graphics.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#valueOptions)
     */
    @JsonProperty()
    declare valueOptions?: SketchValueOptionsConfiguration;
    /**
     * The Sketch widget's state.
     * Possible Values: "ready" | "disabled" | "active"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#state)
     */
    @JsonProperty()
    declare state: string;

    /**
     * The visible elements that are displayed within the widget.
     * This property provides the ability to turn individual elements of the widget's display on/off.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#visibleElements)
     */
    @JsonProperty()
    declare visibleElements: SketchVisibleElementsConfiguration;

    constructor() {
        super();
    }
}
